import { useState, useEffect, useId } from 'react';

import { useEstaCoroado } from '@/lib/crm/use-coroados';

import { cn } from '@/lib/cn';

interface AvatarProps {
  /** Nome completo — usado pra gerar iniciais (fallback) */
  name: string;
  /** URL da foto, se houver (ex: foto do Google). Se carregar falhar, cai pras iniciais. */
  src?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  /**
   * ITEM 42 (BLOCO H, 26/09/2026) — a COROA do 1º lugar.
   *
   * 🔴 **Quem decide NÃO é o Avatar.** Ele não conhece os números, então não
   * pode saber se o topo é zero nem se houve empate — quem sabe é o ranking.
   * ✅ **Quem decide é o SERVIDOR**: o payload dos rankings traz `coroado` por
   * linha, calculado com `mereceCoroa` (`server/lib/crm/coroa.ts`).
   *
   *   `primeiroLugar={p.coroado}`
   *
   * ⚠️ E para uma pessoa fora do ranking — um card, um lead, um comentário —
   * use `userId`: o Avatar pergunta ao contexto e resolve sozinho.
   *
   * É a régua do A4 (25/09): *quem elege não está julgando; quem pinta está* —
   * o juízo se recalcula onde ele é EXIBIDO, nunca se herda de onde o dado
   * nasceu.
   *
   * ⚠️ `false` (o padrão) renderiza **exatamente** a árvore de sempre: nenhum
   * consumidor antigo muda de comportamento.
   */
  primeiroLugar?: boolean;

  /**
   * 🔴🔴 **O ID DA PESSOA — e com ele a coroa aparece SOZINHA, no sistema
   * inteiro.**
   *
   * > **Matheus, 27/09/2026:** *"quando um usuário tá com a coroa no ranking,
   * > em TODO o sistema a coroa deverá aparecer nele também"*
   *
   * ⚠️ **Sem isto, cada tela teria de descobrir quem é o primeiro** — e o card
   * do Kanban não tem ranking nenhum para consultar. ✅ Com o `userId`, o
   * Avatar pergunta ao contexto (`useEstaCoroado`) e o resto vem de graça:
   * **uma consulta por tela, não uma por avatar.**
   *
   * 🔴 **`primeiroLugar` continua mandando mais**, e é de propósito: a tela do
   * ranking JÁ SABE quem coroou (o payload traz `coroado` por linha) e não
   * deve depender de uma segunda consulta para desenhar a própria coroa.
   * ⚠️ Quem passa os dois está dizendo *"eu sei melhor"*, e está certo.
   *
   * ⚠️ **Sem `userId` nada muda:** o Avatar continua exatamente como sempre foi.
   */
  userId?: string | null;
}


/**
 * A coroa — **SVG inline**, nunca imagem: acompanha o tamanho do avatar sem
 * borrar e herda a cor do tema.
 *
 * 🔴 **`absolute`, e isso é requisito, não estilo.** Fora do fluxo, a coroa não
 * ocupa altura: sem isso a PRIMEIRA linha do ranking ficaria mais alta que as
 * outras e a lista inteira desalinharia — o defeito seria visível em toda linha
 * menos naquela que se estava olhando.
 *
 * **A FORMA vem da referência que o Matheus mandou**
 * (`docs/mockups/coroa-referencia-do-matheus.png`): **cinco pontas altas, cada
 * uma com uma ESFERA no topo · pontas MENORES alternadas entre elas, também com
 * esferas · base larga com DOIS FRISOS horizontais · inclinada para a
 * direita.**
 *
 * 🔴 **É a SILHUETA dela, não o render.** A referência é 3D, com dezenas de
 * reflexos, e **no tamanho real esta coroa tem 24 a 40px de largura** (a largura
 * do avatar). Nesse tamanho reflexo vira sujeira e ponta fina some. ⚠️ **O que
 * ficou de fora ficou de propósito:** brilho especular, contorno, sombra
 * projetada e a curvatura da aba — **detalhe que não se vê a 32px é peso e
 * ruído.**
 * ⚠️ **E o arquivo que veio chamado `coroa_inline.svg` NÃO é SVG:** é um
 * `<image>` com um PNG de 303 KB em base64 dentro. **Nome não é conteúdo** —
 * raster borra ao escalar, não troca de cor e pesa 300 KB por linha de ranking.
 *
 * - **82% da largura da foto, centrada** (`w-[82%] left-[9%]`). ⚠️ **Ela já foi
 *   `w-full`, e a medição derrubou:** o círculo é REDONDO, e nas pontas da base
 *   a borda dele ainda está baixa — uma coroa da largura inteira apoia as
 *   pontas no CANTO VAZIO da caixa, não na foto.
 * - **Apoiada na cabeça** (`-top-[28%]`): medido, as duas pontas da base caem
 *   **dentro do círculo**, com 7,6px e 2,1px de folga numa foto de 24px.
 *   ⚠️ **A primeira tentativa foi `top-0`, e o print derrubou:** a coroa cobria
 *   metade da foto e tapava o rosto.
 * - **Inclinada ~10° para a direita, UMA vez só:** o desenho nasce **em pé** e a
 *   inclinação é do CSS. ⚠️ **Desenhar torto e ainda girar inclinaria duas
 *   vezes.** O sentido é o da referência — **a ponta da direita sobe**, que em
 *   CSS é giro anti-horário (`-rotate`).
 *   🔴 **Eram 20°, e o Matheus derrubou olhando o print** (*"a coroa está fora
 *   da foto"*): 20° sobre 19px de altura criavam **8,3px de desnível** entre as
 *   pontas da base — a esquerda afundava e a direita saía. Com 10° o desnível
 *   é de 2,9px. **Ver o bloco de comentário no `className` abaixo, que tem a
 *   medição inteira.**
 * - **DOURADA, em degradê** — ver `DOURADO` logo abaixo.
 * - `pointer-events-none`: o ranking inteiro é clicável (BLOCO I) e a coroa não
 *   pode roubar o clique da linha.
 */

/**
 * 🔴 **O DOURADO MORA AQUI, EM HEX LITERAL, E NÃO É DESCUIDO.**
 *
 * **Medido em `tailwind.config.ts` (26/09/2026):** `amber`, `yellow` e `gold`
 * **não existem** neste tema; `warning` existe e é **`#775420`** — um
 * marrom-mostarda escuro ("warning re-escurecido na auditoria de 13/08"), que
 * **não é dourado**.
 *
 * ⚠️ **A armadilha do BUGS #402:** classe de cor que não existe no tema **não
 * pinta nada e não dá erro** — o Tailwind não emite a regra, o build passa, o
 * gate passa, e a coroa fica **invisível**. Uma classe de preenchimento pedindo
 * `gold`, ou de texto pedindo `amber`, sumiria em silêncio.
 *
 * 🟢 **Medido ao escrever isto:** a primeira versão deste comentário citava as
 * duas classes mortas pelo nome, e **a trava `classe-de-cor-morta` reprovou o
 * COMENTÁRIO** — ela varre o arquivo, não o JSX. Trava que lê prosa acusa
 * documentação honesta; fica o registro, porque o próprio BUGS #402 nomeia
 * essas classes e quem for citá-las em código vai esbarrar nisto.
 *
 * ✅ **Por isso a cor vai no `fill` do SVG, em hex, e não numa classe:** hex
 * literal não depende do tema, então não há classe morta possível. **E é
 * DEGRADÊ, não chapado** — é o degradê que faz ler *dourada* em vez de
 * *mostarda*.
 */
const DOURADO = { claro: '#F6E27A', meio: '#D4AF37', escuro: '#A67C00', friso: '#8A6508' } as const;

/**
 * As pontas, em coordenadas do `viewBox` 24×19. **Cinco altas e quatro
 * menores, alternadas** — é a contagem da referência, e ela é o que faz a
 * silhueta ser lida como coroa e não como serra.
 *
 * A altura das pontas desenha um arco: a do meio é a mais alta e as das pontas
 * são as mais baixas.
 */
const PONTAS_ALTAS = [
  { x: 2.4, y: 5.0 },
  { x: 7.2, y: 3.4 },
  { x: 12.0, y: 2.6 },
  { x: 16.8, y: 3.4 },
  { x: 21.6, y: 5.0 },
];
const PONTAS_BAIXAS = [
  { x: 4.85, y: 8.0 },
  { x: 9.6, y: 7.3 },
  { x: 14.4, y: 7.3 },
  { x: 19.15, y: 8.0 },
];
/** O fundo dos vales, entre uma ponta e a seguinte. */
const VALE_Y = 11.7;
/** Topo e base da aba. */
const ABA_TOPO = 12.6;
const ABA_BASE = 18.6;

/**
 * O contorno fechado: sobe pela aresta esquerda da aba, percorre as nove
 * pontas com um vale entre cada duas, desce pela aresta direita e fecha na
 * base.
 */
function contornoDaCoroa(): string {
  const topo: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < PONTAS_ALTAS.length; i++) {
    topo.push(PONTAS_ALTAS[i]);
    const baixa = PONTAS_BAIXAS[i];
    if (baixa) {
      topo.push({ x: (PONTAS_ALTAS[i].x + baixa.x) / 2, y: VALE_Y });
      topo.push(baixa);
      topo.push({ x: (baixa.x + PONTAS_ALTAS[i + 1].x) / 2, y: VALE_Y });
    }
  }
  const linhas = topo.map((p) => `L${p.x} ${p.y}`).join(' ');
  return `M1.7 ${ABA_TOPO} ${linhas} L22.3 ${ABA_TOPO} L21.5 ${ABA_BASE} L2.5 ${ABA_BASE} Z`;
}

function CoroaPrimeiroLugar() {
  /**
   * 🔴 **Id ÚNICO por instância.** Dois `<linearGradient>` com o mesmo id na
   * mesma página colidem e uma coroa herda o gradiente da outra — defeito que
   * **só aparece quando há mais de um ranking na tela**, que é exatamente o
   * caso das quatro grades do item 31.
   * ⚠️ `useId()` devolve algo como `:r3:`; os dois-pontos saem porque
   * `url(#:r3:)` é caminho de problema em navegador.
   */
  const gradiente = `coroa-${useId().replace(/:/g, '')}`;

  const ouro = `url(#${gradiente})`;

  return (
    <svg
      viewBox="0 0 24 19"
      role="img"
      aria-label="primeiro lugar"
      /**
       * 🔴🔴 **A COROA DESCEU E DESTORCEU em 27/09/2026 — o Matheus olhou o
       * print e disse que ela estava FORA da foto. Ele estava certo, e a
       * medição mostrou por quê.**
       *
       * ⚠️ **A caixa dizia que estava tudo bem:** medida no navegador, ela
       * cobria **9,6px de uma foto de 24px — 40%**. 🔴 Mas a caixa é a
       * **rotacionada**: com 20° de inclinação sobre uma coroa mais larga que
       * alta, ela infla e **os cantos ficam vazios**. O que a pessoa vê é a
       * BASE do desenho, não a caixa.
       *
       * **A conta que importa, com a base em `y=18,6` do `viewBox` 24×19:**
       *
       * | | com 20° | com 10° |
       * |---|---|---|
       * | ponta ESQUERDA da base, dentro da foto | 8,3px | 9,3px |
       * | ponta DIREITA da base, dentro da foto | **1,8px** | 7,3px |
       * | desnível entre as duas | **8,3px** | 3,3px |
       *
       * 🔴 **1,8px num círculo de 24px é FORA**, e não por pouco: o círculo é
       * REDONDO, e naquela altura a borda dele ainda está 2,8px abaixo. A
       * ponta direita pairava no canto VAZIO da caixa.
       *
       * ✅ **A cura foi nos dois eixos, e o principal é a inclinação:** 20°
       * sobre 19px de altura criava 8px de desnível entre as pontas da base —
       * a esquerda afundava na foto enquanto a direita saía dela. Com 10° o
       * desnível cai para 3,3px e as duas pontas assentam.
       *
       * ⚠️ **E por que não só descer:** para a ponta direita entrar, com 20° a
       * coroa teria de descer 4,2px — e aí a ESQUERDA cobriria **52% da foto**,
       * que é o defeito que o `top-0` original já tinha causado (*"cobria
       * metade da foto e tapava o rosto"*). **Descer sem destorcer troca um
       * defeito pelo outro.**
       *
       * 🔴 **A régua que fica: caixa rotacionada não mede desenho.** Quando o
       * elemento gira, `getBoundingClientRect` responde sobre a caixa e o olho
       * responde sobre a forma — e as duas respostas divergem justamente nos
       * cantos.
       */
      className="absolute left-[19%] -top-[31%] w-[62%] h-auto z-10 -rotate-[10deg] pointer-events-none"
      data-testid="avatar-coroa"
    >
      <defs>
        {/* A luz vem de CIMA À ESQUERDA e o degradê escurece para baixo — é o
            degradê que faz ler "dourada" em vez de "mostarda". */}
        <linearGradient id={gradiente} x1="0.1" y1="0" x2="0.55" y2="1">
          <stop offset="0%" stopColor={DOURADO.claro} />
          <stop offset="55%" stopColor={DOURADO.meio} />
          <stop offset="100%" stopColor={DOURADO.escuro} />
        </linearGradient>
      </defs>

      <path d={contornoDaCoroa()} fill={ouro} />

      {/* As esferas das pontas — o que mais distingue esta coroa de um zigue-zague. */}
      {PONTAS_ALTAS.map((p) => (
        <circle key={`a${p.x}`} cx={p.x} cy={p.y - 1.1} r="1.35" fill={ouro} />
      ))}
      {PONTAS_BAIXAS.map((p) => (
        <circle key={`b${p.x}`} cx={p.x} cy={p.y - 0.8} r="1.05" fill={ouro} />
      ))}

      {/* Os DOIS FRISOS da aba. ⚠️ Medidos no print em 24px: mais finos que
          isto viram uma linha só e a aba perde o relevo. */}
      <rect x="2.2" y="14.1" width="19.6" height="0.75" fill={DOURADO.friso} opacity="0.55" />
      <rect x="2.3" y="16.3" width="19.4" height="0.75" fill={DOURADO.friso} opacity="0.55" />
    </svg>
  );
}

const SIZE_CLASSES = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
  '2xl': 'w-24 h-24 text-2xl',
};

/**
 * Avatar com iniciais (Oswald italic) OU imagem com fallback defensivo.
 *
 * Comportamento:
 *  - `src` vazio/null → iniciais
 *  - `src` válido → imagem
 *  - `src` válido mas load falha (404, link expirado, CORS) → cai pras iniciais
 *  - `referrerPolicy="no-referrer"` é crítico pra fotos do Google
 *    (`lh3.googleusercontent.com`) — sem isso o Google bloqueia hotlink
 *    quando o referrer é localhost ou domínio não autorizado.
 *
 * Exemplos:
 *   <Avatar name="Matheus Mussi" />              // → "MM"
 *   <Avatar name="X" src={user.fotoUrl} />       // imagem; iniciais se falhar
 *   <Avatar name="Y" src={fotoUrl} size="2xl" /> // grande pra tela de perfil
 *   <Avatar name="Z" src={fotoUrl} primeiroLugar /> // com a coroa do 1º lugar
 */
export function Avatar({
  name,
  src,
  size = 'md',
  className,
  primeiroLugar = false,
  userId,
}: AvatarProps) {
  /**
   * ⚠️ O hook roda SEMPRE (regra dos hooks), mas ele só lê um `Set` do
   * contexto — sem `userId`, a resposta é `false` e nada muda.
   * 🔴 **`primeiroLugar` vence**: quem já sabe não precisa perguntar.
   */
  const coroadoPeloContexto = useEstaCoroado(userId);
  const temCoroa = primeiroLugar || coroadoPeloContexto;
  const [imgError, setImgError] = useState(false);

  // Reset do erro quando o src muda (caso de refresh de foto)
  useEffect(() => {
    setImgError(false);
  }, [src]);

  const initials = getInitials(name);
  const showImage = !!src && !imgError;

  const circulo = (
    <div
      className={cn(
        'rounded-full bg-red text-white font-display italic flex items-center justify-center tracking-tight flex-shrink-0 overflow-hidden',
        SIZE_CLASSES[size],
        className
      )}
      aria-label={name}
    >
      {showImage ? (
        <img
          src={src!}
          alt={name}
          referrerPolicy="no-referrer"
          onError={() => setImgError(true)}
          className="w-full h-full object-cover"
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );

  // 🔴 Sem coroa a árvore é EXATAMENTE a de sempre — nenhum embrulho a mais.
  // O Avatar é canônico e está em 25 lugares; mudança de estrutura para quem
  // não pediu coroa seria mexer em 25 telas sem ninguém ter olhado.
  if (!temCoroa) return circulo;

  // Com coroa: embrulho `relative` do TAMANHO DO CÍRCULO (o círculo é quem tem
  // largura/altura fixas e `flex-shrink-0`), a coroa `absolute` por cima.
  // ⚠️ O embrulho NÃO tem `overflow-hidden`: a coroa passa do círculo de
  // propósito, e recortá-la cortaria as pontas.
  return (
    <span className="relative inline-flex flex-shrink-0 leading-none">
      {circulo}
      <CoroaPrimeiroLugar />
    </span>
  );
}

function getInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return 'U';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
